import { atom, atomFamily } from "recoil";
import { selector, selectorFamily } from "./recoilSelectors";
import { jobIdsState, jobsByMemberDayState, jobsForUnassignedVisibleState, jobState } from "./jobsState";
import { searchMatch } from "../utils/jobUtils";
import {
    ALL_JOBS_ALL_COLUMNS,
    ALL_JOBS_DEFAULT_COLUMNS,
    ALL_JOBS_DEFAULT_COLUMNS_WIDTH,
    ALL_JOBS_GRID_MEMBER_TYPE,
    ALL_JOBS_JOB_GROUP,
    ALL_JOBS_MEMBER_ALL_COLUMNS,
    ALL_JOBS_MEMBER_DEFAULT_COLUMNS,
    ALL_JOBS_MEMBER_DEFAULT_COLUMNS_WIDTH,
    ALL_JOBS_NUMBER_COLUMN,
    DEFAULT_SORT_CONFIG,
    FORM_FIELD_COLUMN_PREFIX
} from "../constants/jobs";
import { ColumnsWidthType, Day, Filter, PinnedColumnsType, UidDay } from "../types/core";
import { AllJobsGridType, FORM_FIELD_INPUT_TYPES, Job, STATUS_ARCHIVED, STATUS_DELETED } from "../types/job";
import { standardFormContentState } from "./formsState";
import { allJobsActiveTabState } from "./viewState";
import { SORT_DIRECTION, SortDirection } from "baseui/table";
import { DAY, dayOfMinus, dayOfToday, dayToMillisBrowserTimezone } from "../utils/time";
import { atomWithPersistence, localStorageEffect } from "../utils/persistence";
import { companyFieldState } from "./accountState";
import { templatesState } from "./templatesState";
import { dispatchJobVisibilityRequestState } from "./dispatchJobVisibilityState";
import { matchesDispatchJobVisibility } from "../utils/dispatchJobVisibility";
import { filterJobsBySelection } from "./jobFiltersState";

export const allJobsRequestRoundState = atom({
    key: "alljobs.requestround",
    default: 0,
});

export const allJobsSearchState = atom({
    key: "alljobs.search",
    default: "",
});

export type AllJobsSearchScope = "range" | "all";

export const allJobsSearchScopeState = atom<AllJobsSearchScope>({
    key: "alljobs.search.scope",
    default: "all",
});

export const allJobsRemoteHasMoreState = atom({
    key: "alljobs.remote.hasmore",
    default: false,
});

export const allJobsRemoteTotalCountState = atom<number | undefined>({
    key: "alljobs.remote.totalcount",
    default: undefined,
});

export const allJobsTimeframeTotalCountState = atom<number | undefined>({
    key: "alljobs.timeframe.totalcount",
    default: undefined,
});

export const allJobsRemoteSearchingState = atom<boolean>({
    key: "alljobs.remote.searching",
    default: false,
});

export const ALL_JOBS_REMOTE_LIMIT_DEFAULT = 1_000;

export const allJobsRemoteLimitState = atom<number>({
    key: "alljobs.remote.limit",
    default: ALL_JOBS_REMOTE_LIMIT_DEFAULT,
});

/** The jobs a server query returned (search, date choice or server-side job filters), or null while the list shows the loaded range. */
export const allJobsRemoteJobIdsState = atom<string[] | null>({
    key: "alljobs.remote.ids",
    default: null,
});

/** Jobs per status over the whole range, from a server that counts them; undefined from older servers. */
export const allJobsServerStatusCountsState = atom<Record<string, number> | undefined>({
    key: "alljobs.remote.statuscounts",
    default: undefined,
});

/** Jobs per status over the whole range before any filter, sent with the range's first page by a server that counts them. */
export const allJobsRangeStatusCountsState = atom<Record<string, number> | undefined>({
    key: "alljobs.range.statuscounts",
    default: undefined,
});

export const allJobsLoadedIdsState = atom<string[] | null>({
    key: "alljobs.loaded.ids",
    default: null,
});

/** The loaded jobs of the range, the search and the drilldown, before the Jobs bar's filters; the bar counts these. */
export const allJobsBeforeJobFiltersState = selector<Job[]>({
    key: "alljobs.base",
    get: ({ get }) => {
        const allIds = get(jobIdsState);
        const loadedIds = get(allJobsLoadedIdsState);
        const ids = loadedIds !== null ? loadedIds : allIds;
        const search = get(allJobsSearchState);
        const filters = get(allJobsFiltersState);
        const activeTab = get(allJobsActiveTabState);
        const jobs = ids.map(id => get(jobState(id)))
            .filter(job => {
                // loadedIds is a remote-search snapshot that is not pruned on delete
                if (!job || job.status === STATUS_DELETED) {
                    return false;
                }
                return activeTab === "archived"
                    ? job.status === STATUS_ARCHIVED
                    : job.status !== STATUS_ARCHIVED;
            }) as Job[];
        return jobs
            .filter(job => searchMatch(job, search))
            .filter(job => filters.every(f => f.matches(job)));
    }
});

export const dangerousAllJobsState = selector<Job[]>({
    key: "alljobs",
    get: ({ get }) => filterJobsBySelection(get, get(allJobsBeforeJobFiltersState)),
});

export const visibleAllJobsState = selectorFamily<Job[], boolean>({
    key: "alljobs.visible",
    get: (applyDispatchTeamVisibility) => ({ get }) => {
        const jobs = get(dangerousAllJobsState);
        if (!applyDispatchTeamVisibility) {
            return jobs;
        }
        const visibility = get(dispatchJobVisibilityRequestState);
        return jobs.filter((job) => matchesDispatchJobVisibility(job, visibility));
    },
});

export const allJobsGridState = selectorFamily<Job[], {
    uidDay?: UidDay,
    unassigned?: boolean,
    templates?: boolean,
    applyDispatchTeamVisibility?: boolean,
}>({
    key: "alljobs.grid",
    get: ({ uidDay, unassigned = false, templates = false, applyDispatchTeamVisibility = false }) => ({ get }) => {
        if (uidDay) {
            return get(jobsByMemberDayState(uidDay));
        }
        if (unassigned) {
            return get(jobsForUnassignedVisibleState(applyDispatchTeamVisibility));
        }
        if (templates) {
            return get(templatesState(false))
        }
        return get(visibleAllJobsState(applyDispatchTeamVisibility));
    },
});

export const allJobsCountState = selector({
    key: "alljobs.count",
    get: ({ get }) => {
        return get(dangerousAllJobsState).length;
    }
});

/** the member filter a statistics drilldown leaves behind, rendered as a removable chip */
export const DRILL_MEMBER_FILTER_ID = "drill-member";

export const allJobsFiltersState = atom({
    key: "alljobs.filters",
    default: [] as Filter[],
});

export const allJobsSortConfigState = selector({
    key: "alljobs.sortconfig",
    get: ({ get }) => {
        const days: [Day, Day] = get(allJobsFromUntilDaysState);
        const activeTab = get(allJobsActiveTabState);
        return {
            ...DEFAULT_SORT_CONFIG,
            status: activeTab === "archived" ? 1 : 0,
            from: dayToMillisBrowserTimezone(days[0]),
            until: dayToMillisBrowserTimezone(days[1]) + DAY,
        }
    }
});

export const LAST_10_DAYS = () => dayOfMinus(10);
export const LAST_30_DAYS = () => dayOfMinus(30);
export const LAST_6_MONTS = () => dayOfMinus(30 * 6);
export const LAST_10_DAYS_KEY = "10days";
export const LAST_30_DAYS_KEY = "30days";
export const LAST_6_MONTS_KEY = "6months";
export const CUSTOM_DATES_KEY = "custom";

export type AllJobsRangeKey = typeof LAST_10_DAYS_KEY | typeof LAST_30_DAYS_KEY | typeof LAST_6_MONTS_KEY | typeof CUSTOM_DATES_KEY;

export const allJobsFromUntilRangeState = atomWithPersistence<AllJobsRangeKey>(
    "alljobs.fromuntil.range",
    LAST_10_DAYS_KEY,
);

export const allJobsFromUntilDaysState = atom<[Day, Day]>({
    key: "alljobs.fromuntil.day",
    default: selector({
        key: "jobs.table.fromuntil.day.default",
        get: ({ get }) => {
            const selectedRange = get(allJobsFromUntilRangeState);
            if (selectedRange === LAST_10_DAYS_KEY) {
                return [LAST_10_DAYS(), dayOfToday()];
            } else if (selectedRange === LAST_30_DAYS_KEY) {
                return [LAST_30_DAYS(), dayOfToday()];
            } else if (selectedRange === LAST_6_MONTS_KEY) {
                return [LAST_6_MONTS(), dayOfToday()];
            } else {
                return [LAST_10_DAYS(), dayOfToday()];
            }
        }
    })
});

export enum JobSort {
    TITLE = 1,
    ASSIGNEE,
    PROGRESS_STATUS,
    ASSIGNED_DATE,
    PRIORITY,
    ADDRESS,
    TEAM,
    CREATED,
    CREATED_BY,
    SAVED,
    ORDER,
    NOTES,
    TIME_WINDOW,
    ZONES,
    WORK_TYPE,
    CARRY_OVER,
    ONSITE,
    PICKUP,
    DROPOFF,
    LABELS,
    CUSTOM,
    NUMBER,
}

export const allJobsSortState = atomFamily<number, AllJobsGridType>({
    key: "alljobs.sort",
    default: (type) => {
        if (type === ALL_JOBS_GRID_MEMBER_TYPE) {
            return JobSort.NUMBER;
        }
        return JobSort.TITLE;
    },
});

export const allJobsSortDirectionState = atomFamily<SortDirection, AllJobsGridType>({
    key: "alljobs.sort.direction",
    default: SORT_DIRECTION.ASC,
});

export const allJobsColumnsState = atomFamily<string[], AllJobsGridType>({
    key: "alljobs.columns",
    default: (type) => {
        if (type === ALL_JOBS_GRID_MEMBER_TYPE) {
            return ALL_JOBS_MEMBER_DEFAULT_COLUMNS;
        }
        return ALL_JOBS_DEFAULT_COLUMNS;
    },
    effects: [localStorageEffect],
});

export const allJobsCustomSortFieldState = atomFamily<string | undefined, AllJobsGridType>({
    key: "alljobs.sort.custom",
    default: undefined,
});

// Built-in form fields ("Staff Input") exposed as opt-in grid columns, derived
// from the company standard form. Column ids are prefixed so they never collide
// with system columns or company custom fields. Empty when no standard form exists.
export const allJobsFormFieldColumnsState = selector<string[]>({
    key: "alljobs.formFieldColumns",
    get: ({ get }) => {
        const standardForm = get(standardFormContentState);
        const names = (standardForm?.content ?? [])
            .filter((item) => FORM_FIELD_INPUT_TYPES.includes(item.type) && !!item.name?.trim())
            .map((item) => item.name.trim());
        return Array.from(new Set(names)).map((name) => FORM_FIELD_COLUMN_PREFIX + name);
    },
});

export const allJobsGridColumnsState = selectorFamily<{ customFields: string[]; formFieldColumns: string[]; disabledColumns: string[]; enabledColumns: string[] }, AllJobsGridType>({
    key: "alljobs.grid.columns",
    get: (type) => ({ get }) => {
        const jobCustomFields: string[] = get(companyFieldState("job_custom_fields")) ?? [];
        const formFieldColumns: string[] = get(allJobsFormFieldColumnsState);
        const jobGroupsEnabled = get(companyFieldState("job_groups_enabled")) ?? false;
        const baseColumns = type === ALL_JOBS_GRID_MEMBER_TYPE ? ALL_JOBS_MEMBER_ALL_COLUMNS : ALL_JOBS_ALL_COLUMNS;
        const supportedColumns = jobGroupsEnabled ? baseColumns : baseColumns.filter((column) => column !== ALL_JOBS_JOB_GROUP);
        const allColumns = Array.from(new Set([...supportedColumns, ...jobCustomFields, ...formFieldColumns]));
        const enabledColumns: string[] = (get(allJobsColumnsState(type)) ?? []).filter((column) => jobGroupsEnabled || column !== ALL_JOBS_JOB_GROUP);
        const disabledColumns = allColumns.filter((column) => !enabledColumns.includes(column));

        return {
            customFields: jobCustomFields,
            formFieldColumns,
            disabledColumns,
            enabledColumns,
        }
    }
});

export const allJobsGridColumnsWidthState = atomFamily<ColumnsWidthType, AllJobsGridType>({
    key: "alljobs.grid.columns.width",
    default: selectorFamily({
        key: "ag.jobs.table.columns.width.initializer",
        get: (type) => ({ get }) => {
            const customFields: string[] = get(companyFieldState("job_custom_fields")) ?? [];
            const customFieldEntries = customFields.map((customField) => [customField, 100]);
            const customFieldsWidth = Object.fromEntries(customFieldEntries);
            const formFieldColumns: string[] = get(allJobsFormFieldColumnsState);
            const formFieldsWidth = Object.fromEntries(formFieldColumns.map((column) => [column, 150]));
            return {
                ...(type === ALL_JOBS_GRID_MEMBER_TYPE
                    ? ALL_JOBS_MEMBER_DEFAULT_COLUMNS_WIDTH
                    : ALL_JOBS_DEFAULT_COLUMNS_WIDTH),
                ...customFieldsWidth,
                ...formFieldsWidth,
            }
        }
    }),
    effects: [localStorageEffect]
});

export const allJobsGridPinnedColumnsState = atomFamily<PinnedColumnsType, AllJobsGridType>({
    key: "alljobs.grid.columns.pinned",
    default: selectorFamily({
        key: "ag.jobs.table.columns.pinned.initializer",
        get: (type) => ({ get }) => {
            const customFields: string[] = get(companyFieldState("job_custom_fields")) ?? [];
            const customFieldEntries = customFields.map((customField) => [customField, false]);
            const customFieldsPinned = Object.fromEntries(customFieldEntries);
            const formFieldColumns: string[] = get(allJobsFormFieldColumnsState);
            const formFieldsPinned = Object.fromEntries(formFieldColumns.map((column) => [column, false]));
            const columns = type === ALL_JOBS_GRID_MEMBER_TYPE ? ALL_JOBS_MEMBER_ALL_COLUMNS : ALL_JOBS_ALL_COLUMNS;
            const columnsEntries = columns.map((column) => [column, false]);
            const columnsPinned = Object.fromEntries(columnsEntries);

            return {
                ...columnsPinned,
                ...customFieldsPinned,
                ...formFieldsPinned,
                [ALL_JOBS_NUMBER_COLUMN]: true,
            }
        }
    }),
    effects: [localStorageEffect]
});
